import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN, FeatureSettingsType, FinancesViewModel, FinancesViewModelTypes } from '@pxl/models/common';
import { FinancesService, LoginService, UserService, VeterinarianService } from '@pxl/models/contracts';
import {
    FeaturesEffectiveModel,
    FinanceModel,
    FinanceService as FinanceBackendService,
    InvoiceGroupModel,
    VeterinarianModel,
} from '@pxl/shared/generated';
import { getFinanceViewModel } from './finances.function';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class DefaultFinancesService implements FinancesService {
    private readonly financesBackendService = inject(FinanceBackendService);
    private readonly loginService = inject(LoginService);
    private readonly veterinariansService = inject(VeterinarianService);
    private readonly userService = inject(UserService);
    private readonly config = inject(CONFIG_TOKEN);

    public async getFinancesViewModels(): Promise<FinancesViewModel[]> {
        const finances = await firstValueFrom(
            this.financesBackendService.getFinanceInformation(this.config.API_VERSION, this.loginService.getToken() ?? 'TODO'),
        );

        if (!finances) return [];

        const veterinarianIds = DefaultFinancesService.getDistinctVeterinarianIds(finances);
        const [veterinariansMap, featuresEffectiveModel] = await Promise.all([
            this.veterinariansService.getVeterinariansMap(veterinarianIds, (vetId: string) => vetId),
            this.userService.getEffectiveFeatureSettingsForUserAndVeterinarian(veterinarianIds, [FeatureSettingsType.OnlinePayment]),
        ]);

        return DefaultFinancesService.mapFinancesIntoViewModel(finances, veterinariansMap, featuresEffectiveModel).sort(
            DefaultFinancesService.sortFinances,
        );
    }

    public getInvoice(invoiceNumber: string, veterinarianId: string): Promise<InvoiceGroupModel> {
        return firstValueFrom(
            this.financesBackendService.getInvoice(
                veterinarianId,
                invoiceNumber,
                this.config.API_VERSION,
                this.loginService.getToken() ?? '',
            ),
        );
    }

    private static mapFinancesIntoViewModel(
        finances: FinanceModel,
        veterinariansMap: Map<string, VeterinarianModel>,
        featuresEffectiveModel: FeaturesEffectiveModel,
    ): FinancesViewModel[] {
        const financesView: FinancesViewModel[] = [];

        finances.invoices.forEach((invoice) => {
            const { veterinarianId, mentionedVeterinarianIds, grossAmount } = invoice.mostRecent;
            const type: FinancesViewModelTypes = grossAmount < 0 ? FinancesViewModelTypes.Credit : FinancesViewModelTypes.Invoice;

            financesView.push(
                getFinanceViewModel(
                    type,
                    veterinariansMap.get(veterinarianId),
                    [],
                    invoice,
                    null,
                    featuresEffectiveModel,
                    mentionedVeterinarianIds.map((vId) => veterinariansMap.get(vId)),
                ),
            );
        });
        finances.statements.forEach((statement) => {
            const { veterinarianId, mentionedVeterinarianIds } = statement;
            financesView.push(
                getFinanceViewModel(
                    FinancesViewModelTypes.Statement,
                    veterinariansMap.get(veterinarianId),
                    [],
                    null,
                    statement,
                    featuresEffectiveModel,
                    mentionedVeterinarianIds.map((vId) => veterinariansMap.get(vId)),
                ),
            );
        });
        return financesView;
    }

    private static getDistinctVeterinarianIds(finances: FinanceModel): string[] {
        const vetIds = new Set<string>();

        finances.invoices.forEach((invoice) => {
            const { veterinarianId, mentionedVeterinarianIds } = invoice.mostRecent;
            vetIds.add(veterinarianId);
            mentionedVeterinarianIds.forEach((vId) => vetIds.add(vId));
        });
        finances.statements.forEach((statement) => {
            const { veterinarianId, mentionedVeterinarianIds } = statement;
            vetIds.add(veterinarianId);
            mentionedVeterinarianIds.forEach((vId) => vetIds.add(vId));
        });

        return Array.from(vetIds);
    }

    private static sortFinances(a: FinancesViewModel, b: FinancesViewModel): number {
        if (a.invoiceView && b.invoiceView) {
            const haveSameInvoiceDate: boolean =
                a.invoiceView.invoice.mostRecent.invoiceDate === b.invoiceView.invoice.mostRecent.invoiceDate;
            /**
             * Attention: Invoices and credit notes are sorted after the invoiceDate field,
             * which is correct because an invoice or credit note could be sent afterward to petsXL and the createdUtc field would be incorrect.
             *
             * If the comparing invoice or credit note has the same date, sort with the createdUtc field.
             */
            if (haveSameInvoiceDate) {
                return a.invoiceView.invoice.mostRecent.createdUtc < b.invoiceView.invoice.mostRecent.createdUtc ? 1 : -1;
            }
        }
        return a.dateForSorting.isAfter(b.dateForSorting, 'seconds') ? -1 : 1;
    }
}
